import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Crown, Lock, ChevronRight, Smartphone, BookOpen } from 'lucide-react';
import SEO from '../../components/SEO';

const ExploreTimeline = () => {
  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  // Limited preview entries - just titles and years, not full details
  const previewEntries = [
    { year: '1958', title: 'Birth of a Legend', summary: 'Gary, Indiana', icon: '🌟' },
    { year: '1964', title: 'The Jackson Brothers', summary: 'A family begins', icon: '🎵' },
    { year: '1969', title: 'Motown Debut', summary: 'I Want You Back', icon: '🎤' },
    { year: '1971', title: 'Solo Career Begins', summary: 'Got to Be There', icon: '⭐' },
    { year: '1979', title: 'Off the Wall', summary: 'A solo superstar emerges', icon: '🔥' },
    { year: '1982', title: 'Thriller', summary: 'Music history is made', icon: '👑' },
    { year: '1983', title: 'Motown 25', summary: 'The moonwalk debut', icon: '👟' },
    { year: '1984', title: 'Grammy Dominance', summary: '8 awards in one night', icon: '🏆' },
    { year: '1985', title: 'We Are The World', summary: 'A global movement', icon: '🌍' },
    { year: '1987', title: 'Bad Era', summary: 'King of Pop crowned', icon: '🔥' },
  ];

  const lockedEntries = [
    { year: '1988-1990', title: 'Neverland & World Tours', locked: true },
    { year: '1991-1993', title: 'Dangerous Era', locked: true },
    { year: '1993', title: 'Allegations & Aftermath', locked: true },
    { year: '1994-1996', title: 'Marriage & HIStory', locked: true },
    { year: '1997-2000', title: 'Blood on the Dance Floor', locked: true },
    { year: '2001-2005', title: 'Invincible & Trial', locked: true },
    { year: '2005', title: 'The Trial - Full Analysis', locked: true },
    { year: '2006-2008', title: 'Comeback Plans', locked: true },
    { year: '2009', title: 'This Is It', locked: true },
    { year: 'Legacy', title: 'Continuing Impact', locked: true },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="timeline-page">
      <SEO
        title="Michael Jackson Timeline — Complete Life Story from Gary to This Is It | PopCrown"
        description="Explore Michael Jackson's complete timeline. From Gary Indiana and Jackson 5, through Thriller and Bad eras, to This Is It. Every milestone, every album, every breakthrough."
        keywords="Michael Jackson timeline, MJ life story, Michael Jackson history, Jackson 5, Thriller era, Bad tour, Dangerous world tour, HIStory, This Is It, Michael Jackson biography, King of Pop timeline, Gary Indiana, Motown, Off the Wall"
        canonical="/explore/timeline"
      />
      {/* Header */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4">
          <Link to="/explore" className="inline-flex items-center gap-2 text-[#E10600] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" />
            Back to Explore
          </Link>
          <div className="flex items-center gap-4">
            <Clock className="w-12 h-12 text-[#DC143C]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                <span className="thriller-text">Timeline</span>
              </h1>
              <p className="text-gray-400">Journey through the complete history (1958-2009)</p>
            </div>
          </div>
        </div>
      </section>

      {/* App-Only Notice */}
      <section className="py-6" style={{ background: '#E10600' }}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-3 text-black">
            <Smartphone className="w-5 h-5" />
            <p className="font-semibold text-center">
              Full timeline details with photos, sources & deep analysis available exclusively in the PopCrown app
            </p>
          </div>
        </div>
      </section>

      {/* Preview Timeline */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold mb-4">
                PREVIEW - 10 Key Moments
              </span>
              <h2 className="text-2xl font-bold text-black mb-2">The Early Years</h2>
              <p className="text-gray-500 text-sm">Summary view only • Full details in app</p>
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#E10600] to-[#DC143C]"></div>
              
              {previewEntries.map((entry, index) => (
                <div key={index} className="relative pl-20 pb-6">
                  {/* Year Marker */}
                  <div className="absolute left-0 w-16 h-16 rounded-full flex items-center justify-center text-2xl" style={{
                    background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)',
                    boxShadow: '0 4px 20px rgba(225, 6, 0, 0.4)'
                  }}>
                    {entry.icon}
                  </div>
                  
                  {/* Content - Simplified preview only */}
                  <div className="p-4 rounded-xl" style={{
                    background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                    border: '2px solid rgba(225, 6, 0, 0.2)'
                  }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[#E10600] font-bold">{entry.year}</span>
                        <h3 className="text-lg font-bold text-white">{entry.title}</h3>
                        <p className="text-gray-500 text-sm">{entry.summary}</p>
                      </div>
                      <div className="flex items-center gap-1 text-gray-500 text-xs">
                        <Lock className="w-3 h-3" />
                        <span>Details in app</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Locked Content */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#E10600]/20 text-[#DC143C] rounded-full text-sm font-semibold mb-4">
                <Lock className="w-4 h-4" />
                MEMBERS ONLY IN APP
              </span>
              <h2 className="text-2xl font-bold text-black mb-2">Continue the Journey</h2>
              <p className="text-gray-600">40+ additional entries with full analysis</p>
            </div>

            {/* Locked Entries Preview */}
            <div className="space-y-3 mb-8">
              {lockedEntries.map((entry, index) => (
                <div key={index} className="p-4 rounded-xl flex items-center justify-between" style={{
                  background: 'rgba(0, 0, 0, 0.03)',
                  border: '2px dashed rgba(225, 6, 0, 0.3)'
                }}>
                  <div className="flex items-center gap-4">
                    <Lock className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-[#DC143C] font-semibold text-sm">{entry.year}</span>
                      <h4 className="text-gray-600 font-medium">{entry.title}</h4>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              ))}
            </div>

            {/* What Members Get */}
            <div className="p-6 rounded-xl mb-8" style={{ background: '#f8f8f8', border: '1px solid #eee' }}>
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#DC143C]" />
                What's included in the full timeline?
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="text-[#E10600]">✓</span> Detailed narratives for each event
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="text-[#E10600]">✓</span> Historical photos & media
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="text-[#E10600]">✓</span> Source citations & references
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="text-[#E10600]">✓</span> Behind-the-scenes insights
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="text-[#E10600]">✓</span> Court documents & evidence
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="text-[#E10600]">✓</span> Interactive exploration
                </div>
              </div>
            </div>

            {/* Unlock CTA */}
            <div className="p-8 rounded-xl text-center" style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
              border: '2px solid #E10600'
            }}>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Smartphone className="w-8 h-8 text-[#E10600]" />
                <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-10 h-10 object-contain" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                Get the <span className="thriller-text">Full Timeline</span> in the App
              </h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Access 50+ detailed entries with photos, verified sources, and the complete Fact Check timeline - only in the PopCrown app.
              </p>
              <a href={subscriptionJoinLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block">
                Join PopCrown
              </a>
              <p className="text-gray-500 text-sm mt-4">PopCrown Community · £1.99/month</p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-black">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 text-xs">
            Timeline compiled from public records and verified sources. PopCrown is not affiliated with any artist estates or rights holders.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ExploreTimeline;
